import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { X, Mic, MicOff, AlertTriangle } from "lucide-react";
// Import the GIF files
import browserErrorGif from "../../Assets/Videos/Trialpage/browsererror.gif";
import systemErrorGif from "../../Assets/Videos/Trialpage/systemerror.gif";

const MicrophoneErrorPopup = ({ isOpen, onClose, errorType }) => {
  useEffect(() => {
    // Add debugging to see what error type is being received
    if (isOpen) {
      console.log("MicrophoneErrorPopup opened with errorType:", errorType);
    }
  }, [isOpen, errorType]);

  if (!isOpen) return null;

  // Function to get error details based on the error type
  const getErrorDetails = () => {
    switch (errorType) {
      case "browser_permission":
        return {
          title: "Microphone is Blocked - Can You Enable It?",
          message:
            "To use the reading features, please turn on the microphone. Click the little icon at the top of your screen and allow the microphone. Then, reload the page to get started!",
          
          imageSrc: browserErrorGif, // Use imported GIF
          icon: <AlertTriangle size={40} className="text-amber-500" />,
          bgColor: "bg-amber-50",
          buttonColor: "bg-amber-600 hover:bg-amber-700",
        };
      case "system_permission":
        return {
          title: "Oops! Your Headphones Aren't Connected!",
          message:
            "To use the reading features, connect a microphone to your device, then turn it on. Click the little camera icon at the top of your screen, allow the microphone, and reload the page!",
          imageSrc: systemErrorGif, // Use imported GIF
          icon: <MicOff size={40} className="text-red-500" />,
          bgColor: "bg-red-50",
          buttonColor: "bg-red-600 hover:bg-red-700",
        };
      case "no_device":
        return {
          title: "No Microphone Detected",
          message: "We couldn't detect a microphone connected to your device.",
          imageSrc: systemErrorGif, // Reusing system error GIF - add a specific one if available
          icon: <Mic size={40} className="text-gray-400" />,
          bgColor: "bg-gray-100",
          buttonColor: "bg-gray-600 hover:bg-gray-700",
        };
      default:
        return {
          title: "Microphone Error",
          message: "There was an issue with your microphone.",
          steps: [
            "Check if your microphone is connected properly",
            "Make sure no other applications are using your microphone",
            "Try closing and reopening your browser",
            "Refresh the page to try again",
          ],
          imageSrc: browserErrorGif, // Fallback to browser error GIF
          icon: <AlertTriangle size={40} className="text-blue-500" />,
          bgColor: "bg-blue-50",
          buttonColor: "bg-blue-600 hover:bg-blue-700",
        };
    }
  };

  const { title, message, steps, imageSrc, icon, bgColor, buttonColor } =
    getErrorDetails();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700">
          <X size={20} />
        </button>

        <div className="flex flex-col items-center text-center">
          <div
            className={`w-48 h-48 mb-4 rounded-lg ${bgColor} flex items-center justify-center overflow-hidden`}>
            <img
              src={imageSrc}
              alt="Microphone Error"
              className="w-full h-full object-contain"
            />
          </div>

          <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>

          <div className={`${bgColor} p-4 rounded-lg mb-6 text-center w-full`}>
            <p className="text-gray-600 mb-4">{message}</p>
          </div>

          <button
            onClick={onClose}
            className={`${buttonColor} text-white font-medium py-2 px-6 rounded-full transition-colors`}>
            Got it
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MicrophoneErrorPopup;